import { useQuery } from '@tanstack/react-query'
import { fetchMetaData, DEFAULT_META } from '../data/metaApi.js'

/**
 * Top-level hook for the full meta object.
 * Same pattern as useGoalsData — everything that lives in meta
 * (comments, weekly summaries, reactions, DMs, etc.) reads from this
 * single cached source, so components stay consistent.
 *
 * Cache key: ['meta']
 *
 * Refetch cadence: once a minute as a safety net. Real-time updates come
 * from Ably (useRealtimeSync), so aggressive polling is unnecessary and
 * actually harmful — a GET to KV within ~60 seconds of a PUT can return
 * stale data (KV eventual consistency). A 60s interval is slow enough
 * that KV has always propagated by the time we refetch.
 *
 * refetchOnWindowFocus is off for the same reason: tab-switching right
 * after a save would hit stale KV and resurrect deleted items. Ably
 * handles cross-tab sync.
 *
 * Returns:
 *   data — the meta object (never undefined — falls back to DEFAULT_META)
 *   isLoading / isFetching / error — standard React Query flags
 */
export function useMetaData() {
  const q = useQuery({
    queryKey: ['meta'],
    queryFn: fetchMetaData,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  })
  return {
    ...q,
    data: q.data || DEFAULT_META,
  }
}
